import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

interface EmailCampaign {
  id: string
  name: string
  sent: number
  delivered: number
  opens: number
  unique_opens: number
  clicks: number
  unsubscribes: number
  bounces: number
  open_rate: number
  click_rate: number
  ctr: number
}

interface AutomationFlow {
  id: string
  name: string
  active_subscribers: number
  emails_sent: number
  avg_open_rate: number
  avg_click_rate: number
  conversions: number
  revenue: number
}

interface Statistics {
  total_subscribers: number
  avg_open_rate: number
  avg_click_rate: number
  total_emails_sent: number
  total_revenue: number
  list_growth_rate: number
}

export default function EmailAnalyticsPage() {
  const [campaigns, setCampaigns] = useState<EmailCampaign[]>([])
  const [automations, setAutomations] = useState<AutomationFlow[]>([])
  const [statistics, setStatistics] = useState<Statistics | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    try {
      const [campaignsRes, automationsRes, statsRes] = await Promise.all([
        fetch('/api/emailanalytics/campaigns'),
        fetch('/api/emailanalytics/automations'),
        fetch('/api/emailanalytics/statistics')
      ])
      if (campaignsRes.ok) setCampaigns(await campaignsRes.json())
      if (automationsRes.ok) setAutomations(await automationsRes.json())
      if (statsRes.ok) setStatistics(await statsRes.json())
    } catch (error) {
      console.error('Error fetching email analytics data:', error)
    } finally {
      setLoading(false)
    }
  }

  const formatNumber = (value: number) => {
    return new Intl.NumberFormat('en-US').format(value)
  }

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value)
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Email Marketing Analytics</h1>
          <p className="mt-2 text-gray-600">Phase 246 - Email campaign performance</p>
        </div>

        {statistics && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Subscribers</p>
              <p className="text-2xl font-bold text-gray-900">{formatNumber(statistics.total_subscribers)}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Open Rate</p>
              <p className="text-2xl font-bold text-blue-600">{statistics.avg_open_rate}%</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Click Rate</p>
              <p className="text-2xl font-bold text-green-600">{statistics.avg_click_rate}%</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Emails Sent</p>
              <p className="text-2xl font-bold text-purple-600">{formatNumber(statistics.total_emails_sent)}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Revenue</p>
              <p className="text-2xl font-bold text-green-600">{formatCurrency(statistics.total_revenue)}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">List Growth</p>
              <p className="text-2xl font-bold text-orange-600">+{statistics.list_growth_rate}%</p>
            </div>
          </div>
        )}

        <div className="bg-white rounded-lg shadow mb-6">
          <div className="px-6 py-4 border-b">
            <h2 className="text-lg font-semibold">Email Campaigns</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Campaign</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Sent</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Delivered</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Open Rate</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Click Rate</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">CTR</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Bounces</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {campaigns.map((campaign) => (
                  <tr key={campaign.id}>
                    <td className="px-6 py-4 font-medium text-gray-900">{campaign.name}</td>
                    <td className="px-6 py-4 text-gray-500">{formatNumber(campaign.sent)}</td>
                    <td className="px-6 py-4 text-green-600">{formatNumber(campaign.delivered)}</td>
                    <td className="px-6 py-4 text-blue-600 font-bold">{campaign.open_rate}%</td>
                    <td className="px-6 py-4 text-purple-600">{campaign.click_rate}%</td>
                    <td className="px-6 py-4 text-green-600">{campaign.ctr}%</td>
                    <td className="px-6 py-4 text-red-600">{formatNumber(campaign.bounces)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b">
            <h2 className="text-lg font-semibold">Automation Flows</h2>
          </div>
          <div className="p-6 space-y-4">
            {automations.map((flow) => (
              <div key={flow.id} className="p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-gray-900">{flow.name}</span>
                  <span className="text-lg font-bold text-green-600">{formatCurrency(flow.revenue)}</span>
                </div>
                <div className="grid grid-cols-5 gap-2 text-sm">
                  <div className="text-center p-2 bg-white rounded">
                    <p className="text-gray-500">Subscribers</p>
                    <p className="font-medium">{formatNumber(flow.active_subscribers)}</p>
                  </div>
                  <div className="text-center p-2 bg-white rounded">
                    <p className="text-gray-500">Emails Sent</p>
                    <p className="font-medium text-blue-600">{formatNumber(flow.emails_sent)}</p>
                  </div>
                  <div className="text-center p-2 bg-white rounded">
                    <p className="text-gray-500">Open Rate</p>
                    <p className="font-medium text-purple-600">{flow.avg_open_rate}%</p>
                  </div>
                  <div className="text-center p-2 bg-white rounded">
                    <p className="text-gray-500">Click Rate</p>
                    <p className="font-medium text-green-600">{flow.avg_click_rate}%</p>
                  </div>
                  <div className="text-center p-2 bg-white rounded">
                    <p className="text-gray-500">Conversions</p>
                    <p className="font-medium text-orange-600">{flow.conversions}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </main>
    </div>
  )
}
